import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/ui/Popover'
import { Icon, IconName } from '@/ui/icon2'
import rootStore from '@/app/store'
import { useNavigate } from 'react-router-dom'
import { useDialogRef } from '@/utils/dialog'
import { ShortcutsDialog } from '@/features/sidebar/components/Shortcuts'
import { truncate } from '@/utils/string'
import cx from 'classnames'
import styles from './index.module.css'
import React from 'react'
import { Tooltip } from '@/ui/Tooltip2/index'

export const User = ({
  narrow,
  hideOnlyDesktopLinks,
  isCollapsed,
}: {
  narrow?: boolean
  hideOnlyDesktopLinks?: boolean
  isCollapsed?: boolean
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const {
    dialogRef: shortcutsDialogRef,
    handleOpen: openShortcuts,
    handleClose: closeShortcuts,
  } = useDialogRef()

  const MENU_LINKS = {
    stats: {
      to: '/stats',
      icon: 'stats',
      label: 'Estadísticas',
    },
    api: {
      to: '/api',
      icon: 'code',
      label: 'API',
    },
    documents: {
      to: '/knowledge-bases/global',
      icon: 'archive',
      label: 'Documentos AiKit',
    },
    history: {
      to: '/history',
      icon: 'history',
      label: 'Historial',
    },
    images: {
      to: '/history-images',
      icon: 'image',
      label: 'Historial de imágenes',
      hiddenOnMobile: true,
    },
    connections: {
      to: '/connections',
      icon: 'settings',
      label: 'Conexiones',
    },
    accountSettings: {
      to: '/account-settings',
      icon: 'settings',
      label: 'Ajustes de cuenta',
    },
  } as Record<
    string,
    { to: string; icon: IconName; label: string; hiddenOnMobile?: boolean }
  >

  const filteredMenuLinks = hideOnlyDesktopLinks
    ? Object.fromEntries(
        Object.entries(MENU_LINKS).filter(([, link]) => !link.hiddenOnMobile),
      )
    : MENU_LINKS

  const MENU_ACTIONS = {
    support: {
      action: () => handleSupport(),
      icon: 'cmd',
      label: 'Soporte',
    },
    shortcuts: {
      action: () => handleShortcuts(),
      icon: 'cmd',
      label: 'Atajos de teclado',
    },
    logout: {
      action: () => handleLogout(),
      icon: 'logout',
      label: 'Cerrar sesión',
    },
  } as Record<string, { action: () => void; icon: IconName; label: string }>

  type MenuLinkKey = keyof typeof MENU_LINKS
  type UserRole = 'admin' | 'org-manager' | 'user'

  const ROLE_LINKS: Record<UserRole, MenuLinkKey[]> = {
    admin: ['documents', 'history', 'images', 'connections', 'accountSettings'],
    'org-manager': [
      'stats',
      'history',
      'images',
      'connections',
      'accountSettings',
    ],
    user: ['history', 'images', 'connections', 'accountSettings'],
  } as const

  const navigate = useNavigate()

  const {
    auth,
    tools,
    organizations,
    knowledgeBases,
    chat,
    tool,
    voiceChat,
    support,
  } = rootStore

  const isValidUserRole = (role: string): role is UserRole => {
    return ['admin', 'org-manager', 'user'].includes(role)
  }

  const getUserRole = (role: string): UserRole => {
    return isValidUserRole(role) ? role : 'user'
  }

  const getMenuLinks = (role: UserRole) => {
    return ROLE_LINKS[role]
      .map((link) => filteredMenuLinks[link])
      .filter((link) => link !== undefined)
  }

  const handleLogout = () => {
    auth.logout().then(() => {
      tools.resetState()
      chat.resetState()
      tool.resetState()
      voiceChat.resetState()
      organizations.resetState()
      knowledgeBases.resetState()

      navigate('/auth/login?redirect=/home')
    })
  }

  const handleSupport = () => {
    support.setOpen(true)
  }

  const handleShortcuts = () => {
    openShortcuts()
  }

  const loggedInUser = auth.user!

  return (
    <div className={styles.container} data-screenshots-id="user-menu-dropdown">
      <DropdownMenu onOpenChange={setIsOpen}>
        <Tooltip
          content={
            <div className={styles.tooltip}>
              {loggedInUser.organizationName && (
                <div
                  className={styles.organizationName}
                  data-screenshots-id="user-menu-organization-name"
                >
                  {loggedInUser.organizationName}
                </div>
              )}
              <div
                className={styles.email}
                data-screenshots-id="user-menu-email"
              >
                {loggedInUser.email}
              </div>
            </div>
          }
          dark
          delay={0}
          sideOffset={12}
          dataScreenshotsId="user-menu-tooltip"
        >
          <DropdownMenuTrigger
            className={cx(styles.trigger, { [styles.collapsed]: isCollapsed })}
          >
            <div aria-hidden="true" className={styles.avatar}>
              {auth.avatarUrl ? (
                <img src={auth.avatarUrl} alt="Avatar" />
              ) : (
                <Icon size={28} name="user" />
              )}
            </div>
            {!narrow && (
              <>
                <span
                  className={styles.name}
                  data-screenshots-id="user-menu-name"
                >
                  {truncate(loggedInUser.email.split('@')[0], 16).toLowerCase()}
                </span>
                <Icon
                  className={cx(styles.icon, { [styles.isOpen]: isOpen })}
                  size={14}
                  name="chevron-down"
                />
              </>
            )}
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent side="top" align="center">
          {getMenuLinks(getUserRole(auth.user?.role ?? 'user')).map((link) => (
            <DropdownMenuItem key={link.to} to={link.to}>
              <Icon name={link.icon} />
              <span>{link.label}</span>
            </DropdownMenuItem>
          ))}
          {Object.values(MENU_ACTIONS).map((action) => (
            <DropdownMenuItem key={action.label} onClick={action.action}>
              <Icon name={action.icon} />
              <span>{action.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ShortcutsDialog
        dialogRef={shortcutsDialogRef}
        onClose={closeShortcuts}
      />
    </div>
  )
}
